import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Card, CardContent } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAgeSections } from '../../store/slices/ageSectionSlice';
import { updateKid } from '../../store/slices/kidSlice';
import { flowLogger } from '../../utils/flowLogger';

const isUniversalAgeSection = (ageSection) => {
  const name = (ageSection.name || '').toLowerCase();
  if (name.includes('tous') && (name.includes('âge') || name.includes('age'))) {
    return true;
  }
  if (ageSection.min_age === 0 && ageSection.max_age >= 99) {
    return true;
  }
  return false;
};

const AgeSelectionStep = ({ kid, onNext }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ageSections, loading: ageLoading, error: ageError } = useSelector((state) => state.ageSection);
  const { loading: kidLoading, error: kidError } = useSelector((state) => state.kid);

  const [selectedAgeSection, setSelectedAgeSection] = useState(null);

  const selectableAgeSections = useMemo(
    () => (ageSections || []).filter((section) => !isUniversalAgeSection(section)),
    [ageSections]
  );

  useEffect(() => {
    if (!ageSections || ageSections.length === 0) {
      dispatch(fetchAgeSections());
    }
  }, [dispatch, ageSections]);

  const handleRetry = () => {
    dispatch(fetchAgeSections());
  };

  const handleAgeSelect = (ageSection) => {
    setSelectedAgeSection(ageSection);
  };

  const handleNext = async () => {
    if (!selectedAgeSection || !kid) return;

    try {
      await dispatch(updateKid({
        id: kid.id,
        ages_section_id: selectedAgeSection.id,
      })).unwrap();

      flowLogger('profiling.age.selected', {
        kidId: kid.id,
        ageSectionId: selectedAgeSection.id,
      });

      onNext({
        ...kid,
        ages_section_id: selectedAgeSection.id,
        age_section: selectedAgeSection,
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'enfant:', error);
    }
  };

  if (!kid) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#FFF9F0',
          px: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: '500px' }}>
          Impossible de charger le profil enfant. Veuillez recommencer.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/Profilling')} sx={{ backgroundColor: '#784e3c' }}>
          Recommencer
        </Button>
      </Box>
    );
  }

  if (ageLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#FFF9F0',
        }}
      >
        <CircularProgress size={60} />
        <Typography sx={{ ml: 2 }}>Chargement des tranches d'âge...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#FFF9F0',
        px: 3,
        pt: 6,
      }}
    >
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <img
          src="/assets/homepage.png"
          alt="Enfant joyeux"
          style={{
            width: '180px',
            height: '180px',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: '#2C3E50',
          mb: 2,
          textAlign: 'center',
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontFamily: 'Achiko',
        }}
      >
        Quel âge a {kid.firstname} ?
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          color: '#666',
          mb: 4,
          maxWidth: '500px',
        }}
      >
        Sélectionnez la tranche d'âge qui correspond à {kid.firstname} pour personnaliser les questions.
      </Typography>

      {(ageError || kidError) && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: '600px' }}>
          {ageError || kidError}
        </Alert>
      )}

      {selectableAgeSections.length === 0 ? (
        <Box sx={{ textAlign: 'center', maxWidth: '500px', mb: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Aucune tranche d'âge disponible pour le moment.
          </Alert>
          <Button variant="outlined" onClick={handleRetry} sx={{ borderColor: '#784e3c', color: '#784e3c' }}>
            Réessayer
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 4,
          }}
        >
          {selectableAgeSections.map((ageSection) => (
            <Card
              key={ageSection.id}
              onClick={() => handleAgeSelect(ageSection)}
              sx={{
                cursor: 'pointer',
                border: selectedAgeSection?.id === ageSection.id ? '3px solid #784e3c' : '3px solid transparent',
                borderRadius: '20px',
                transition: 'all 0.3s ease',
                boxShadow: selectedAgeSection?.id === ageSection.id
                  ? '0px 4px 15px rgba(181, 129, 54, 0.3)'
                  : '0px 2px 8px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0px 4px 15px rgba(181, 129, 54, 0.2)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: selectedAgeSection?.id === ageSection.id ? 'bold' : 'normal',
                    color: '#2C3E50',
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                    fontFamily: 'Achiko',
                  }}
                >
                  {ageSection.name}
                </Typography>
                {ageSection.description && (
                  <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                    {ageSection.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Button
        onClick={handleNext}
        disabled={!selectedAgeSection || kidLoading || selectableAgeSections.length === 0}
        variant="contained"
        fullWidth
        sx={{
          maxWidth: '600px',
          backgroundColor: '#784e3c',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          textTransform: 'none',
          borderRadius: '50px',
          py: 1.5,
          boxShadow: '0px 4px 10px rgba(181, 129, 54, 0.4)',
          fontFamily: 'Achiko',
          '&:hover': {
            backgroundColor: '#B58136',
            boxShadow: '0px 6px 15px rgba(181, 129, 54, 0.6)',
          },
          '&:disabled': {
            backgroundColor: '#D0D0D0',
            color: '#888888',
          },
        }}
      >
        {kidLoading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Mise à jour...
          </>
        ) : (
          'Continuer vers les questions'
        )}
      </Button>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: '#666',
          mt: 2,
          maxWidth: '400px',
        }}
      >
        Cette information nous aide à adapter les questions à l'âge de {kid.firstname}.
      </Typography>
    </Box>
  );
};

export default AgeSelectionStep;
